<template>
  <tr
    class="participants-course-steps-item-row"
    :class="courseStep.done ? 'active' : ''"
  >
    <td class="participants-course-steps-item-col d-flex align-center">
      <v-avatar
        width="56"
        height="56"
        rounded
        :class="
          `participants-course-steps-item__thumbnail mr-3 ${
            hasFile ? 'participants-course-steps-item__thumbnail--selected' : ''
          }`
        "
      >
        <v-img
          :src="thumbnail"
          :lazy-src="require('@/assets/placeholder_other.png')"
          :alt="courseStep.title"
          :key="
            `course-step-list-item-thumbnail-${courseStep.id}-${thumbnailKey}`
          "
          @error="onThumbnailError"
          @click="showPreview"
        >
          <template v-slot:placeholder>
            <div class="d-flex align-center fill-height justify-center">
              <v-progress-circular
                indeterminate
                color="accent"
              ></v-progress-circular>
            </div>
          </template>
        </v-img>
      </v-avatar>
      <div class="participants-course-steps-item__name">
        <h4
          class="participants-course-steps-item__title"
          :title="courseStep.title"
        >
          {{ courseStep.title }}
        </h4>
        <p
          class="participants-course-steps-item__description"
          v-if="courseStep.description"
          :title="courseStep.description"
        >
          {{ courseStep.description }}
        </p>
      </div>
    </td>
    <td class="participants-course-steps-item-col">
      <span class="text-nowrap">
        <div class="d-flex align-center">
          <rating-stars
            color="#945EFF"
            :rating="courseStep.rating"
            :size="12"
          />&nbsp;<strong>{{ courseStep.rating }}</strong>
        </div>
      </span>
    </td>
    <td class="participants-course-steps-item-col">
      <v-btn
        v-if="courseStep.link"
        :href="courseStep.link"
        text
        dense
        color="accent"
        class="rounded-pill"
        target="_blank"
        >{{ courseStep.link }}</v-btn
      >
    </td>

    <td class="participants-course-steps-item-col">
      <span class="bhGray2--text text-nowrap">
        {{ courseStep.finishedDate | moment('MMMM Do, YYYY') }}
      </span>
    </td>
    <td class="participants-course-steps-item-col">
      <v-chip small color="bhGray6" v-if="courseStep.done"
        ><span
          class="participants-course-steps-item__dot participants-course-steps-item__dot--done"
        ></span
        >Done</v-chip
      >
    </td>
  </tr>
</template>

<script>
import RatingStars from '@/components/exercises/RatingStars.vue'
import { debounce } from '@/helpers/debounce'
import globalPreviewMediaMixin from '@/mixins/globalPreviewMedia'
import { getFileTypeByUrl } from '@/helpers/chat'

export default {
  name: 'ParticipantCourseStepsTableRow',
  mixins: [globalPreviewMediaMixin],
  components: {
    RatingStars,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isThumbnailError: false,
      thumbnailKey: 0,
      isLoading: false,
    }
  },
  methods: {
    onThumbnailError() {
      if (this.thumbnailKey >= 3) {
        this.isThumbnailError = true
        return
      }
      debounce(
        () => {
          this.thumbnailKey++
        },
        5000,
        `REFRESH_COURSE_STEP_THUMBNAIL_${this.user?.id}`,
      )
    },
    showPreview() {
      if (this.hasFile) {
        this.openGlobalPreviewMedia({
          mediaUrl:
            this.user?.staticMediaThumbnailUrl ||
            this.user?.mediaUrl ||
            this.user?.thumbnailUrl,
          imageName: null,
          mlResourceUrl: null,
          fileTypeId: null,
        })
      }
    },
  },
  computed: {
    courseStep() {
      return this.user
    },
    thumbnail() {
      if (this.isThumbnailError) {
        const { type } = getFileTypeByUrl(
          this.user?.thumbnailUrl || this.user?.mediaUrl,
        )
        if (type === 'image') {
          return require('@/assets/placeholder_image.png')
        }
        if (type === 'video') {
          return require('@/assets/placeholder_video.png')
        }
        if (type === 'pdf') {
          return require('@/assets/placeholder_pdf.png')
        }
        return require('@/assets/placeholder_other.png')
      }
      return (
        this.user?.staticMediaThumbnailUrl ||
        this.user?.thumbnailUrl ||
        this.user?.mediaUrl ||
        require('@/assets/placeholder_other.png')
      )
    },
    hasFile() {
      return !!(
        this.user?.staticMediaThumbnailUrl ||
        this.user?.thumbnailUrl ||
        this.user?.mediaUrl
      )
    },
  },
}
</script>

<style lang="scss">
.participants-course-steps-item {
  &-row {
    border-radius: $border-radius-root * 2;
    background: transparent !important;
    overflow: hidden;

    &.active {
      .participants-course-steps-item-col {
        background: $bh-white;
      }
    }
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 8px;
    margin-right: 6px;
    background-color: transparent;

    &--done {
      background-color: var(--bh-green);
    }

    &--in-progress {
      background-color: var(--bh-yellow);
    }
  }

  &-col {
    padding: 8px !important;
    border: 0px solid #000;
    border-color: $bh-gray--5 !important;
    border-top-width: 1px;
    border-bottom-width: 1px;
    height: auto !important;

    strong {
      display: inline-block;
      margin-bottom: 4px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    p {
      font-size: 0.857rem;
      line-height: 1rem;
    }

    p:last-child {
      margin-bottom: 0;
    }

    &:first-of-type {
      min-width: 0;
      border-left-width: 1px;
      border-top-left-radius: $border-radius-root * 2;
      border-bottom-left-radius: $border-radius-root * 2;
    }

    &:last-of-type {
      min-width: 0;
      border-right-width: 1px;
      border-top-right-radius: $border-radius-root * 2;
      border-bottom-right-radius: $border-radius-root * 2;
    }
    .v-chip {
      text-transform: capitalize;
      font-weight: 500;
    }
    .text-number {
      font-size: 1.125rem;
    }
  }

  &__title {
    margin-bottom: 0;
    color: #000;
    font-size: 16px;
    line-height: 24px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    max-width: 350px;
  }

  &__description {
    color: rgba(0, 0, 0, 0.6);
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    white-space: normal;
  }

  &__thumbnail--selected {
    cursor: pointer;
  }
}
</style>
